import React, { useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import type { ChartSpec } from "@Interfaces/Entities/AssistantCharts";

type ApexConfig = {
    type: "bar" | "pie" | "scatter";
    series: any;
    options: ApexCharts.ApexOptions;
};

const isFiniteNumber = (value: unknown): value is number =>
    typeof value === "number" && Number.isFinite(value);

const asNonEmptyString = (value: unknown): string | null => {
    if (value === null || value === undefined) return null;
    const str = String(value).trim();
    return str ? str : null;
};

const truncateLabel = (label: string, maxLen: number) => {
    if (label.length <= maxLen) return label;
    return `${label.slice(0, Math.max(0, maxLen - 1))}…`;
};

const pickSeriesName = (yKey: string, chart: ChartSpec, isSingle: boolean) => {
    if (isSingle) return chart.config?.valueLabel || yKey;
    return yKey;
};

export const validateAssistantChartSpec = (
    chart: ChartSpec
): { ok: true } | { ok: false; reason: string } => {
    if (!chart || typeof chart !== "object") {
        return { ok: false, reason: "invalid_chart" };
    }
    if (!chart.title || !chart.whyItMatters) {
        return { ok: false, reason: "missing_metadata" };
    }
    if (!Array.isArray(chart.data) || chart.data.length === 0)
        return { ok: false, reason: "missing_data" };

    switch (chart.chartType) {
        case "pie":
            if (!chart.nameKey || !chart.valueKey)
                return { ok: false, reason: "missing_keys" };
            return { ok: true };
        case "grouped_scatter":
            if (!chart.xKey || !chart.groupKey || !chart.yKeys?.length)
                return { ok: false, reason: "missing_keys" };
            if (chart.yKeys.length !== 1)
                return { ok: false, reason: "invalid_yKeys" };
            return { ok: true };
        case "vertical_bar":
        case "horizontal_bar":
        case "stacked_bar":
            if (!chart.xKey || !chart.yKeys?.length)
                return { ok: false, reason: "missing_keys" };
            if (chart.chartType === "stacked_bar" && chart.yKeys.length < 2)
                return { ok: false, reason: "invalid_yKeys" };
            return { ok: true };
        default:
            return { ok: false, reason: "unsupported_type" };
    }
};

const buildApexConfig = (
    chart: ChartSpec,
    colors: string[],
    noDataText: string
): ApexConfig | null => {
    const showLegend = chart.config?.showLegend ?? true;
    const showTooltip = chart.config?.showTooltip ?? true;

    const baseOptions: Pick<
        ApexCharts.ApexOptions,
        "colors" | "grid" | "chart" | "dataLabels" | "tooltip" | "noData"
    > = {
        colors,
        grid: {
            borderColor: "rgba(148, 163, 184, 0.35)",
            strokeDashArray: 3,
        },
        chart: {
            id: chart.id,
            toolbar: { show: false },
            animations: { enabled: false },
        },
        dataLabels: { enabled: false },
        tooltip: { enabled: showTooltip },
        noData: {
            text: noDataText,
            align: "center",
            verticalAlign: "middle",
            style: { fontSize: "12px" },
        },
    };

    if (chart.chartType === "pie") {
        const nameKey = chart.nameKey ?? undefined;
        const valueKey = chart.valueKey ?? undefined;
        if (!nameKey || !valueKey) return null;

        const rows = (chart.data ?? []).filter((row) => {
            const name = asNonEmptyString(row[nameKey]);
            const value = row[valueKey];
            return Boolean(name) && isFiniteNumber(value);
        });
        if (rows.length === 0) return null;

        const labels = rows.map((r) => String(r[nameKey]));
        const series = rows.map((r) => Number(r[valueKey]));

        return {
            type: "pie",
            series,
            options: {
                ...baseOptions,
                chart: { ...baseOptions.chart, type: "pie" },
                labels,
                legend: {
                    show: showLegend,
                    position: "bottom",
                    fontSize: "12px",
                },
                dataLabels: { enabled: true, style: { fontSize: "11px" } },
            },
        };
    }

    if (chart.chartType === "grouped_scatter") {
        const xKey = chart.xKey ?? undefined;
        const yKey = chart.yKeys?.[0];
        const groupKey = chart.groupKey ?? undefined;
        if (!xKey || !yKey || !groupKey) return null;

        const rows = (chart.data ?? []).filter((row) => {
            const x = row[xKey];
            const y = row[yKey];
            const g = asNonEmptyString(row[groupKey]);
            return isFiniteNumber(x) && isFiniteNumber(y) && Boolean(g);
        });
        if (rows.length === 0) return null;

        const grouped: Record<string, { x: number; y: number }[]> = {};
        for (const row of rows) {
            const g = String(row[groupKey]);
            grouped[g] = grouped[g] || [];
            grouped[g].push({ x: Number(row[xKey]), y: Number(row[yKey]) });
        }

        const series = Object.entries(grouped).map(([name, points]) => ({
            name,
            data: points,
        }));

        return {
            type: "scatter",
            series,
            options: {
                ...baseOptions,
                chart: {
                    ...baseOptions.chart,
                    type: "scatter",
                    zoom: { enabled: true },
                },
                legend: { show: showLegend, fontSize: "12px" },
                xaxis: {
                    type: "numeric",
                    title: { text: chart.config?.xAxisLabel || undefined },
                    labels: { style: { fontSize: "11px" } },
                },
                yaxis: {
                    title: { text: chart.config?.yAxisLabel || undefined },
                    labels: { style: { fontSize: "11px" } },
                },
            },
        };
    }

    const xKey = chart.xKey ?? undefined;
    const yKeys = chart.yKeys ?? [];
    if (!xKey || yKeys.length === 0) return null;
    if (chart.chartType === "stacked_bar" && yKeys.length < 2) return null;

    const rows = (chart.data ?? []).filter((row) => {
        const category = asNonEmptyString(row[xKey]);
        if (!category) return false;
        return yKeys.every((key) => isFiniteNumber(row[key]));
    });
    if (rows.length === 0) return null;

    const categories = rows.map((r) => String(r[xKey]));
    const maxCategoryLen = categories.reduce(
        (acc, c) => Math.max(acc, c.length),
        0
    );

    const isSingleSeries = yKeys.length === 1;
    const series = yKeys.map((key) => ({
        name: pickSeriesName(key, chart, isSingleSeries),
        data: rows.map((r) => Number(r[key])),
    }));

    const orientation = chart.config?.orientation;
    let horizontal = chart.chartType === "horizontal_bar";
    if (orientation === "horizontal") horizontal = true;
    if (orientation === "vertical") horizontal = false;
    const stacked = chart.chartType === "stacked_bar";

    const categoryAxisLabel = chart.config?.xAxisLabel || undefined;
    const valueAxisLabel = chart.config?.yAxisLabel || undefined;
    const xAxisTitle = horizontal ? valueAxisLabel : categoryAxisLabel;
    const yAxisTitle = horizontal ? categoryAxisLabel : valueAxisLabel;

    const shouldRotateX =
        !horizontal && (categories.length > 8 || maxCategoryLen > 14);
    const labelMaxLen = horizontal ? 28 : 14;

    return {
        type: "bar",
        series,
        options: {
            ...baseOptions,
            chart: { ...baseOptions.chart, type: "bar", stacked },
            plotOptions: {
                bar: {
                    horizontal,
                    borderRadius: 4,
                    columnWidth: yKeys.length > 1 ? "60%" : "45%",
                },
            },
            legend: { show: showLegend && yKeys.length > 1, fontSize: "12px" },
            xaxis: {
                categories,
                title: { text: xAxisTitle },
                labels: {
                    style: { fontSize: "11px" },
                    trim: !shouldRotateX,
                    rotate: shouldRotateX ? -45 : 0,
                    rotateAlways: Boolean(shouldRotateX),
                    formatter: (val: string) =>
                        horizontal
                            ? String(val)
                            : truncateLabel(String(val), labelMaxLen),
                },
            },
            yaxis: {
                title: { text: yAxisTitle },
                labels: {
                    style: { fontSize: "11px" },
                    formatter: (val: number) =>
                        horizontal
                            ? truncateLabel(String(val), labelMaxLen)
                            : truncateLabel(String(val), 28),
                },
            },
        },
    };
};

export const GeneratedApexChartRenderer: React.FC<{
    chart: ChartSpec;
    height?: number;
}> = ({ chart, height = 420 }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const renderErrorText = t(
        "pages.eventInsights.assistant.charts.renderErrorTitle"
    );
    const noDataText = t("pages.eventInsights.assistant.charts.noData");

    const colors = useMemo(
        () => [
            theme.palette.primary.main,
            theme.palette.secondary.main,
            theme.palette.info.main,
            theme.palette.success.main,
            theme.palette.warning.main,
            theme.palette.error.main,
        ],
        [theme]
    );

    const validation = useMemo(
        () => validateAssistantChartSpec(chart),
        [chart]
    );
    const config = useMemo(() => {
        if (!validation.ok) return null;
        return buildApexConfig(chart, colors, noDataText);
    }, [chart, colors, noDataText, validation.ok]);

    if (!validation.ok) {
        return (
            <Box sx={{ py: 3 }}>
                <Typography variant="body2" color="text.secondary">
                    {renderErrorText}
                </Typography>
            </Box>
        );
    }

    if (!config) {
        return (
            <Box sx={{ py: 3 }}>
                <Typography variant="body2" color="text.secondary">
                    {renderErrorText}
                </Typography>
            </Box>
        );
    }

    return (
        <ReactApexChart
            type={config.type}
            series={config.series}
            options={config.options}
            height={height}
            width="100%"
        />
    );
};
